/* @flow */

import * as React from 'react'
import styled from 'styled-components'

import ProductItem from '../../ProductBrowser/ProductItem'

import { useShopProducts, useCartActions } from '../../../hooks'
import { removeNonDigits, addPxToInput } from '../../../../shared'
import { WebshopContext } from '../../../shared'
import { WidgetContainer, useWidgetCustomFont } from '../shared'

const WebshopProductCarousel = ({ gridItem, isEditing, ...rest }) => {
  const { brand, loadFont, webshopSession } = React.useContext(WebshopContext)

  const shopRequestArguments = React.useMemo(() => {
    const productIds = gridItem.data.products || []

    const searchOptions = {
      currency: webshopSession.currency,
      filters: [],
      page: 1,
      limit: productIds.length > 0 ? productIds.length : 10,
      sort: 'name',
      webshop_session_id: webshopSession.id,
      webshop_v2: 1,
    }

    if (productIds.length > 0) {
      searchOptions.product_id = productIds
    }

    return [brand.id, searchOptions]
  }, [gridItem, webshopSession.id])

  const [products, isFetchingProducts] = useShopProducts(shopRequestArguments)

  const [headlineFont] = useWidgetCustomFont(
    loadFont,
    gridItem.data.headline_font
  )

  // carousel state
  const [offset, setOffset] = React.useState(0)
  const widthOfEachItem = parseInt(
    removeNonDigits(gridItem.data.product_width || 0)
  ) // Width of each carousel item
  const numberOfItems = products.length // Total number of items in the carousel

  const colorAttributeName = brand.settings.color_attribute
  const colorAttributeColors = brand.settings.color_attribute_colors

  const {
    cartRows: fetchedCartRows,
    cartIsSaving,
    updatedLines: updatedCartLines,
    onLineChange,
    totals,
    isInitialized: isCartRowsInitialized,
    refreshCartRows,
    updateCartLines: onUpdateCart,
  } = useCartActions(brand.id, webshopSession.id, false, false, false)

  const spaceBetweenEachProduct = removeNonDigits(
    gridItem.data.space_between_each_product || 0
  )

  const handlePrevClick = () => {
    // Move to the previous item unless at the start
    setOffset(currentOffset => Math.min(currentOffset + widthOfEachItem, 0))
  }

  const handleNextClick = () => {
    // Move to the next item unless at the end
    setOffset(currentOffset =>
      Math.max(
        currentOffset - widthOfEachItem,
        -(widthOfEachItem * (numberOfItems - 3))
      )
    )
    // Adjust '-3' based on how many items you want visible at a time
  }

  if (products.length === 0) {
    return null
  }

  return (
    <Container gridItem={gridItem}>
      {gridItem.data.headline && gridItem.data.headline.trim() && (
        <HeadlineContainer
          font={headlineFont}
          position={gridItem.data.headline_position}
          dangerouslySetInnerHTML={{ __html: gridItem.data.headline }}
        />
      )}

      <OverflowHiddenContainer>
        <PaddingContainer>
          <ControlButton
            style={{ left: '10px', top: '50%' }}
            onClick={handlePrevClick}
          >
            <span className="glyphicon glyphicon-chevron-left" />
          </ControlButton>

          <CarouselContainer
            spaceBetweenEachProduct={spaceBetweenEachProduct}
            style={{ transform: `translateX(${offset}px)` }}
          >
            {products.map(product => {
              return (
                <ProductItemContainer
                  key={product.id}
                  gridItem={gridItem}
                  spaceBetweenEachProduct={spaceBetweenEachProduct}
                >
                  <StyledProductItem
                    brand={brand}
                    cartIsSaving={cartIsSaving}
                    cartRows={fetchedCartRows}
                    colorAttributeColors={colorAttributeColors}
                    colorAttributeName={colorAttributeName}
                    margin={0}
                    onCartUpdate={onUpdateCart}
                    perRow={1}
                    product={product}
                    refreshCartRows={refreshCartRows}
                    showLabels={showLabels}
                    webshopSession={webshopSession}
                  />
                </ProductItemContainer>
              )
            })}
          </CarouselContainer>

          <ControlButton
            style={{ right: '20px', top: '50%' }}
            onClick={handleNextClick}
          >
            <span className="glyphicon glyphicon-chevron-right" />
          </ControlButton>
        </PaddingContainer>
      </OverflowHiddenContainer>
    </Container>
  )
}

export default WebshopProductCarousel

const showLabels = ['Item Number']

const Container = styled(WidgetContainer)`
  position: relative;
  width: 100%;
`

const OverflowHiddenContainer = styled.div`
  overflow: hidden;
`

const PaddingContainer = styled.div`
  overflow: hidden;
  padding: 1rem 0px;
  position: relative;
`

const CarouselContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-left: ${({ spaceBetweenEachProduct }) =>
    25 - spaceBetweenEachProduct}px;
  position: relative;
  transition: transform 0.3s ease-out; // Smooth transition for the transform
  z-index: 0;
`

const ProductItemContainer = styled.div`
  /* without this the product items will now overflow the container */
  flex-shrink: 0;
  margin-left: ${({ spaceBetweenEachProduct }) =>
    addPxToInput(spaceBetweenEachProduct)};
  width: ${({ gridItem }) => addPxToInput(gridItem.data.product_width)};
`

const StyledProductItem = styled(ProductItem)`
  /* if some brands have multi-line product names then they become higher and we need
  all products in the row to be equal height */
  height: 100%;
`

const ControlButton = styled.button.attrs({
  type: 'button',
})`
  background: white;
  box-shadow: 5px 5px 15px -3px rgba(0, 0, 0, 0.025);
  border-radius: 30px;
  border: none;
  outline: 0;
  height: 45px;
  margin-top: -22px;
  padding: 10px;
  position: absolute;
  z-index: 100;
  width: 45px;

  &:hover {
    background: rgba(255, 255, 255, 0.8);
  }
`

const HeadlineContainer = styled.div`
  font-family: ${({ font }) => font || 'inherit'};
  padding: 20px 20px 5px 20px;
  text-align: ${({ position }) => position};

  > p {
    color: black;
    font-size: 20px;
    margin: 0;
  }
`
